import { createCodePlugin } from '@streamdown/code';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import type { BundledTheme, ThemeRegistrationAny } from 'shiki';

/** A Shiki theme: one it bundles, by id, or a VS Code theme given as data. */
export type CodeTheme = BundledTheme | ThemeRegistrationAny;
/** The code themes of the light and the dark appearance, in that order. */
export type CodeThemePair = readonly [light: CodeTheme, dark: CodeTheme];

export const DEFAULT_CODE_THEMES: CodeThemePair = ['one-light', 'one-dark-pro'];

const CodeThemesContext = createContext<CodeThemePair>(DEFAULT_CODE_THEMES);

/** Gives code blocks below it the app's code themes. */
export function CodeThemesProvider({
  children,
  themes,
}: {
  children: ReactNode;
  themes: CodeThemePair;
}) {
  return <CodeThemesContext.Provider value={themes}>{children}</CodeThemesContext.Provider>;
}

export function useCodeThemes(): CodeThemePair {
  return useContext(CodeThemesContext);
}

const plugins = new WeakMap<CodeThemePair, ReturnType<typeof createCodePlugin>>();

/** Streamdown's code highlighter for the current themes (it caches highlighters by theme name). */
export function useCodeHighlighterPlugin() {
  const themes = useCodeThemes();
  return useMemo(() => {
    let plugin = plugins.get(themes);
    if (!plugin) {
      plugin = createCodePlugin({ themes: [themes[0], themes[1]] });
      plugins.set(themes, plugin);
    }
    return plugin;
  }, [themes]);
}
